import type { CreatedBy } from '/@/components/created-by';
import { _ } from '/@/locale';
import type { Zone } from '../zone';

interface CropState {
    vegetativeStage: number | null;
    phenology: PhenologyStage;
}

export interface CornStageChange {
    id: number;
    date: string;
    manualChange: boolean;
    vegetativeStage: number | null;
    phenology: PhenologyStage | null;
    cornCropMonitoring: number;
}

export type CropType = 'corn' | 'wheat' | 'soy' | 'barley';
export type PhenologyStage = 'fallow' | 'planting' | 'emergence' | 'flowering' | 'maturity' | 'harvested';

export interface YieldEstimationSample {
    id: number;
    name: string;
    spikesPerHectare: number;
    grainsPerSpike: number;
    thousandGrainsWeight: number;
    result: number;
}

export interface YieldEstimation {
    id: number;
    status: string;
    /** Average estimated yield across the samples. */
    result: number;
    yieldSamples: YieldEstimationSample[];
    /** Derived from the earliest sample's timestamp; null when there are no samples. */
    created: string | null;
    /** UserNode id of the author. */
    createdBy: number | null;
}
export interface CornCropMonitoring {
    id: number;
    product: number;
    productName: string;
    accumulatedGdu: number;
    accumulatedGduDate: string;
    expectedFloweringDate: string;
    expectedMaturityDate: string;
    expectedV6Date: string;
    expectedHarvestDate?: string;
    hasGenericProduct: boolean;
    plantingDate: string;
    isPreceon: boolean;
    state: CropState;
    vegetativeStage: number | null;
    phenology: PhenologyStage;
    yieldEstimations: YieldEstimation[];
    stages: CornStageChange[];
    currentStage: 'flowering-maturity' | 'emergence-flowering' | 'sowing-emergence' | 'harvested';
    technology: number;
    fertilizer: number;
    fertilizerAmount: number;
    soilType: number;
}

export interface CropMonitoring {
    id: number;
    plantingDate: string;
    environment: number;
    plantedArea: number | null;
    density: number | null;
    crop: CropType;
    previousCrop?: CropType | 'other';
    campaign: string | null;
    active: boolean;
    field: number;
    fieldArea: number | null;
    fieldName: string;
    created: string;
    modified: string;
    harvestDate: string | null;
    productName?: string;
    cornCropMonitoring?: CornCropMonitoring;
    nitrogenRecomendations: unknown[];
    result?: {
        id: number;
        created: string;
        modified: string;
        humidityAtHarvest: number;
        averageYield: number;
        totalHarvestedArea: number | null;
        /** The hybrids (crop monitorings) sharing this harvest result. */
        cropMonitorings: number[];
        /** The same hybrids, with what names them (ordered by id). */
        hybrids?: ResultHybrid[];
        harvestReport: number | null;
        createdBy?: number;
        updatedBy?: number;
    };
    userNode: number;
    zone?: Zone;
    createdBy?: number;
    updatedBy?: number;
    /** FieldView planting report used to prefill this campaign, when applicable. */
    sourcePlantingReport?: number | null;
    /** The seed of the planting report FieldView created this hybrid from; empty when made by hand. */
    sourceSeedProductName?: string;
}

/** A hybrid sharing a harvest result: the members `getHybridProductName` reads and its own
 * planting data. */
export type ResultHybrid = Pick<CropMonitoring, 'id' | 'sourcePlantingReport' | 'plantingDate'> & {
    productName: string | null;
    density: number | null;
    plantedArea: number | null;
    cornCropMonitoring: Pick<CornCropMonitoring, 'hasGenericProduct' | 'productName' | 'isPreceon'> | null;
};

export type CropMonitoringResultNotes = {
    isLoading: boolean;
    data: {
        count: number;
        results: {
            id: number;
            content: string;
            created: string;
            modified: string;
            createdBy: CreatedBy;
            updatedBy: CreatedBy;
        }[];
    };
};

export type CropMonitoringApplicationReports = {
    isLoading: boolean;
    data: {
        count: number;
        results: {
            id: number;
            type: string;
            field: number;
            growingSeasonId: string;
            status: string;
            fetchRetries: number;
        }[];
    };
};

// Constants - will be localized in components
export const PHENOLOGY_LABEL_KEYS: Record<PhenologyStage, string> = {
    fallow: _('Barbecho'),
    planting: _('Siembra'),
    emergence: _('Emergencia'),
    flowering: _('Floración'),
    maturity: _('Madurez'),
    harvested: _('Cosechado'),
} as const;

export const CROP_MONITORING_KEYS: Partial<Record<CropType, keyof CropMonitoring>> = {
    corn: 'cornCropMonitoring',
    // wheat and soy monitoring keys will be added when those features are implemented
} as const;
